<template>
  <div class="root container">
    <div class="desktop-search-container hide-on-mobile">
      <div class="wrapper">
        <product-hidden-actions
          class="desktop-search"
          :data-show-search="showSearchToggleDesktop"
          @search="searchHidden"
          @searchEnd="searchHiddenEnd"
          @openSort="sortMenuActive = true"
          @openFilter="filterMenuActive = true"
        />
      </div>
    </div>
    <!-- main section -->
    <div
      class="row vcenter actions"
      :class="!searchHiddenBool ? 'active' : null"
    >
      <!-- Products: {{ productsToAdd }} -->
      <input-field
        class="mb-0 search"
        classes="mb-0"
        prefixIcon="search"
        iconClickable
        :placeholder="`Search ${activeProductCategory} products`"
        :icon="searchCloseIcon"
        @iconClick="clearSearch"
        @focus="searchCloseIcon = 'close'"
        @blur="searchBlur"
        @keyup="searchKeyup"
      />
      <div class="buttons">
        <span class="f-btn" @click="sortMenuActive = true">
          <i class="material-icons">sort</i>
          <span>Sort</span>
        </span>
        <span class="f-btn" @click="filterMenuActive = true">
          <i class="material-icons">filter_list</i>
          <span>Filter</span>
        </span>
      </div>
    </div>
    <template v-if="apiLoadingStatus === 1">
      <products-loading />
    </template>
    <template v-else>
      <div class="row products">
        <div
          v-for="product in productsToShow"
          :key="`product-${product.id}`"
          class="product-container"
        >
          <div class="product" @click="gotoProduct(product.id)">
            <div class="img">
              <img
                :src="getProductImage(product)"
                :alt="`Image of Product: ${product.name}`"
              />
              <product-stock-display :product="product" />
            </div>
            <div class="row vcenter product__info">
              <div class="sm-7 meta">
                <span class="product__info__name">{{ product.name }}</span>
                <span>$ {{ product.price }}</span>
              </div>
              <div class="sm-5 text-right">
                <!-- {{ productsToAdd.includes(product.id) }}
                {{ product.stock }} -->
                <button
                  class="waves"
                  :class="{
                    loading: productsToAdd.includes(product.id),
                    outline: !productsToAdd.includes(product.id),
                    disabled: product.stock <= 0,
                  }"
                  :disabled="product.stock <= 0"
                  @click.stop="addToCart(product)"
                >
                  <span
                    :style="`opacity: ${
                      productsToAdd.includes(product.id) ? '0' : '1'
                    }`"
                  >
                    Buy
                  </span>
                </button>
                <!-- <button v-else class="outline waves" @click="addToCart(product)">Buy</button> -->
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row hvcenter end">
        <template v-if="nextPageApiStatus === 1">
          <linear-loader class="loader" text="Getting more products..." />
        </template>
        <div v-if="pageNo === productPages && nextPageApiStatus !== 1">
          No more products
        </div>
      </div>
    </template>

    <div
      class="bottom-nav"
      :class="bottomNavActive ? 'active' : null"
      :data-show-search="showSearchToggle"
    >
      <div class="bottom-nav-actions">
        <cart-button class="cart-button" />
        <button
          class="fab search-toggle hide-on-tablet-and-up"
          @click="toggleBottomNav"
        >
          <i class="material-icons">
            {{ bottomNavActive ? "close" : "search" }}
          </i>
        </button>
      </div>
      <div class="search hide-on-tablet-and-up">
        <product-hidden-actions
          class="search-actions"
          @search="searchHidden"
          @searchEnd="searchHiddenEnd"
          @openSort="sortMenuActive = true"
          @openFilter="filterMenuActive = true"
        />
      </div>
    </div>

    <bottom-sheet :active="sortMenuActive" @close="sortMenuActive = false">
      <h2>Sort By</h2>
      <div class="row vcenter">
        <div class="sm-4">Price</div>
        <select-field
          class="sm-8"
          :options="sortMethods.price"
          @select="selectSortMethod"
        />
      </div>

      <template v-slot:footer>
        <button class="flat mr-1 sheet-close">Cancel</button>
        <button class="waves sheet-close" @click="sortProducts">Sort</button>
      </template>
    </bottom-sheet>
    <bottom-sheet :active="filterMenuActive" @close="filterMenuActive = false">
      <h2>Filter By</h2>
      <div class="row vcenter">
        <div class="sm-5">Product Category</div>
        <select-field
          class="sm-7"
          :options="productCategories"
          @select="selectFilterCategory"
        />
      </div>

      <template v-slot:footer>
        <button class="flat mr-1 sheet-close">Cancel</button>
        <button class="waves sheet-close" @click="filterProducts">
          Filter
        </button>
      </template>
    </bottom-sheet>
  </div>
</template>

<script lang="js">
/*eslint-disable no-undef */
import { mapGetters } from "vuex";

import { getProductPrimaryImage } from "@/utils";
import { AppConfig } from "@/utils/variables";

import ProductStockDisplay from "@/components/product/ProductStockDisplay.vue";
import LinearLoader from "@/components/loaders/LinearLoader";
import CartButton from "@/components/product/CartButton";
import BottomSheet from "@/components/BottomSheet";
import SelectField from "@/components/SelectField";
import InputField from "@/components/InputField";

import ProductsLoading from "./components/ProductsLoading";
import ProductHiddenActions from "./components/ProductHiddenActions";

// TODO New Page sorting and filtering
/*
Apply filter on new page items
For sort:
  --> sort the new page independently
  --> sort the whole page
  --> don't sort at all
*/
export default {
  name: "Products",
  metaInfo() {
    return {
      title: `Home | ${AppConfig.name}`,
    };
  },
  components: {
    LinearLoader,
    CartButton,
    InputField,
    BottomSheet,
    SelectField,
    ProductsLoading,
    ProductStockDisplay,
    ProductHiddenActions,
  },
  computed: {
    ...mapGetters({
      user: "User/getDetails",
      apiLoadingStatus: "Products/getLoadingStatus",

      products: "Products/getAll",
      productPages: "Products/getProductPages",
      nextPageApiStatus: "Products/getNextPageApiStatus",

      cart: "Cart/cart",
      cartQuantity: "Cart/cartQuantity",
      addCartItemStatus: "Cart/addCartItemStatus",
      addCartItemMessage: "Cart/addCartItemMessage",
      logoutStatus: "Auth/logoutStatus",
      logoutMessage: "Auth/logoutMessage",
    }),

    productCategories() {
      return Object.keys(this.productsParsed);
    },
  },
  data() {
    return {
      pageNo: 1,
      productsToAdd: [],
      productsToShow: [],
      productsParsed: {},

      searching: false,
      searchCloseIcon: "",
      searchHiddenBool: false,
      activeProductCategory: "All",
      searchMobileThreshold: 110,
      searchDesktopThreshold: 100,

      filterMenuActive: false,
      sortMenuActive: false,
      sortPriceMethodIndex: 0,
      sortMethods: {
        price: [
          {
            text: "Lowest to Highest",
            name: "l-to-h",
          },
          {
            text: "Highest to Lowest",
            name: "h-to-l",
          },
        ],
      },

      bottomNavActive: false,
      showSearchToggle: false,
      showSearchToggleDesktop: false,
    };
  },
  watch: {
    products(newProducts) {
      // console.log("Products: ", newProducts);
      this.parseProducts(newProducts);
    },
  },
  beforeCreate() {
    this.$store.dispatch("Products/getAll");
  },
  created() {
    this.parseProducts();
  },
  mounted() {
    T.waves();

    window.addEventListener("scroll", () => {
      if (
        window.innerHeight + window.scrollY + 100 >=
        document.body.offsetHeight
      )
        this.fetchNextProductsPage();

      if (
        window.scrollY < this.searchMobileThreshold &&
        !this.searchHiddenBool
      ) {
        this.showSearchToggle = false;
        this.bottomNavActive = false;
      } else this.showSearchToggle = true;

      if (
        window.scrollY < this.searchDesktopThreshold &&
        !this.searchHiddenBool
      )
        this.showSearchToggleDesktop = false;
      else this.showSearchToggleDesktop = true;
    });
  },
  methods: {
    parseProducts(products = this.products) {
      const categories = {
        All: products,
      };

      products.forEach((product) => {
        const category = product.category.name;
        if (Object.keys(categories).includes(category))
          categories[category].push(product);
        else categories[category] = [];
      });

      this.productsParsed = categories;
      this.productsToShow = this.productsParsed[this.activeProductCategory];
    },
    getProductImage(product) {
      return getProductPrimaryImage(product)
    },

    fetchNextProductsPage() {
      // TODO implement global search without fetching new items
      if (this.pageNo !== this.productPages && !this.searching)
        this.$store.dispatch("Products/getByPage", ++this.pageNo);
    },
    clearSearch(_, input) {
      input.value = "";
      input.focus();

      this.searchProducts("");
    },
    searchBlur(e) {
      if (e.target.value.trim() === "") this.searchCloseIcon = "";
    },
    searchProducts(name) {
      if (name.trim() === "") this.searching = false;
      else this.searching = true;

      // always searches for products within the category
      const products = this.productsParsed[this.activeProductCategory];

      const foundProducts = products.filter((product) => {
        return product.name.indexOf(name) > -1;
      });

      this.productsToShow = foundProducts;
      // console.log(name, foundProducts)
    },
    searchKeyup(e) {
      const searchVal = e.target.value;
      this.searchProducts(searchVal);
    },
    searchHidden(e) {
      this.searchHiddenBool = true;
      this.searchKeyup(e);
    },
    searchHiddenEnd() {
      console.log("Called");
      this.searchHiddenBool = false;
      if (window.scrollY < this.searchDesktopThreshold)
        this.showSearchToggleDesktop = false;

      if (window.scrollY < this.searchMobileThreshold) {
        this.showSearchToggle = false;
        this.bottomNavActive = false;
      }
    },
    toggleBottomNav() {
      console.log("B: ", this.bottomNavActive);

      if (this.bottomNavActive) {
        // supposed to close
        if (!this.searchHiddenBool) this.searchHiddenBool = false;

        this.bottomNavActive = false;
      } else this.bottomNavActive = true;
      // this.bottomNavActive = !this.bottomNavActive;

      // if (!this.bottomNavActive && !this.searchHiddenBool)
      //   this.searchHiddenEnd(false)

      console.log("A: ", this.bottomNavActive);
    },

    selectSortMethod(index) {
      this.sortPriceMethodIndex = index;
    },
    sortProducts() {
      this.productsToShow.sort((prev, curr) => {
        const priceMethod = this.sortMethods.price[this.sortPriceMethodIndex]
          .name;

        if (priceMethod === "l-to-h") return prev.price - curr.price;
        return curr.price - prev.price;
      });
    },
    selectFilterCategory(index) {
      this.activeProductCategory = Object.keys(this.productsParsed)[index];
    },
    filterProducts() {
      this.productsToShow = this.productsParsed[this.activeProductCategory];
    },

    addToCart(product) {
      let foundItem = this.cart.find(
        (cartItem) => product.id === cartItem.product.id
      );

      if (foundItem) {
        if (foundItem.quantity >= product.stock) {
          // TODO: notify user that they have added all available productt 'x'
        } else {
          this.performAddToCart(product);
        }
      } else {
        this.performAddToCart(product);
      }
    },
    performAddToCart(product) {
      console.log("Product: ", product.id);

      if (!this.productsToAdd.includes(product.id))
        this.productsToAdd.push(product.id);

      this.$store.dispatch("Cart/addCartItem", product).then(() => {
        if (this.addCartItemStatus === 2) {
          // TODO: notify the user of success
          console.log(`${product.name} Added to cart successfully!`);
        } else if (this.addCartItemStatus === 3) {
          // TODO: notify the user of the error
          console.log("Could not add item to cart!", this.addCartItemMessage);
        }

        this.productsToAdd.splice(this.productsToAdd.indexOf(product.id), 1);
      });
    },
    removeItem(product) {
      const payload = { product, content: { decrement: true } };
      this.$store.dispatch("Cart/removeItem", payload);
    },

    gotoProduct(id) {
      this.$router.push({ name: "ProductDetails", params: { id } });
    },

    logout() {
      this.$store.dispatch("Auth/logout").then(() => {
        if (this.logoutStatus === 2) {
          // TODO: notify the user of successful logout
          console.log(`${this.logoutMessage}`);
          this.$router.push({ name: "Landing" });
          this.$store.commit("Auth/reset");
        } else if (this.logoutStatus === 3) {
          // TODO: notify the user of the error (in a user friendly way)
          console.log(`${this.logoutMessage}`);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.products {
  row-gap: 3ch;
  padding-bottom: 30px;

  .product-container {
    width: 100%;

    @media only screen and (min-width: 450px) {
      width: 50%;
    }
    @media only screen and (min-width: 700px) {
      width: calc(100% / 3);
    }
    @media only screen and (min-width: 992px) {
      width: 25%;
    }

    .product {
      cursor: pointer;
      overflow: hidden;
      border-radius: 5px;
      background-color: white;

      .img {
        height: 200px;
        overflow: hidden;
        position: relative;

        img {
          transition: $transition-fast-out;
        }
      }

      &__info {
        padding: 0.5rem 0.75rem;

        &__name {
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        .meta {
          span {
            display: block;
            opacity: 0.7;

            &:first-child {
              margin-bottom: 0.1rem;
              opacity: 1;
            }
          }
        }
        button {
          padding: 0.5rem 0.75rem;

          span {
            transition: inherit;
          }
        }
      }

      &:hover {
        img {
          transform: scale(1.025);
        }
      }

      @media only screen and (min-width: 450px) {
        width: 90%;
      }
      // width:
    }
  }
}

.actions {
  row-gap: 1ch;
  justify-content: center;
  opacity: 0;
  max-height: 0;
  transition: $transition-mid-in-out;

  .search {
    width: 100%;
  }

  @media only screen and (min-width: 600px) {
    justify-content: space-between;
    margin-bottom: 1.5rem;

    .search {
      width: max-content;
      min-width: 278px;
    }
  }

  .buttons {
    display: flex;

    .f-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border-radius: 5px;
      padding: calc(0.5rem - 2px) 0.75rem;
      border: 2px solid #cccccc;
      background-color: #e7e7e7;
      transition: $transition-fast-out;

      span {
        margin-left: 5px;
      }

      &:hover {
        color: white;
        border-color: $primary-color;
        background-color: $primary-color;
      }

      &:first-child {
        margin-right: 5px;
      }
    }
  }

  &.active {
    opacity: 1;
    max-height: 100px;
    margin-bottom: 1rem;
  }
}

.end {
  padding-bottom: 8rem;

  @media only screen and (min-width: 600px) {
    padding-bottom: 3rem;
  }

  .loader {
    width: 50%;
  }
}

@include mobile-only {
  .bottom-nav {
    width: 100%;
    height: 100px;
    position: fixed;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background-color: transparent;
    transition: $transition-mid-in-out;

    &-actions {
      opacity: 1;
      width: 100%;
      height: 100%;
      position: relative;
      transition: inherit;

      .cart-button,
      .search-toggle {
        position: absolute;
        z-index: 2;
        opacity: 1;
        top: 50%;
        pointer-events: all;
        transform: translate(50%, -50%);
      }

      .search-toggle {
        opacity: 0;
        z-index: 1;
        right: 50%;
      }
    }
    .search {
      position: fixed;
      opacity: 0;
      left: 50%;
      top: 50%;
      z-index: 1;
      height: 100%;
      transition: inherit;
      width: calc(100% - 30px);
      transform: translate(-50%, 0%);

      &-actions {
        width: 100%;
        pointer-events: all;
        transform: translate(-50%, 20%);
        transition: inherit;
      }
    }

    &.active {
      background-color: #ffffff;

      @supports ((backdrop-filter: blur(5px))) {
        background-color: #ffffffca;
        backdrop-filter: blur(20px);
      }

      .bottom-nav-actions {
        .cart-button,
        .search-toggle {
          z-index: 0;
          transition: inherit;
        }
        .cart-button {
          opacity: 0;
        }

        .search-toggle {
          transform: translate(50%, -160%) !important;
        }
      }

      .search {
        opacity: 1;

        .search-actions {
          transform: translate(-50%, -50%);
        }
      }
    }

    &[data-show-search] {
      .bottom-nav-actions {
        .cart-button {
          transform: translate(-50%, -50%);
        }

        .search-toggle {
          opacity: 1;
          transform: translate(100%, -50%);
        }
      }
    }
  }
}
.desktop-search-container {
  width: 100%;
  height: 54px;
  top: 0;
  left: 50%;
  z-index: 999;
  position: fixed;
  pointer-events: none;
  transform: translateX(-50%);

  .wrapper {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
    // background-color: $orange;

    .desktop-search {
      opacity: 0;
      pointer-events: all;
      transform: translate(-50%, 30px);
      transition: $transition-mid-in-out;

      &[data-show-search] {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
  }
}
</style>

<style lang="scss">
.theme-dark {
  .actions .f-btn {
    border-color: #474747 !important;
    background-color: #e7e7e720 !important;

    &:hover {
      border-color: $primary-color !important;
      background-color: $primary-color !important;
    }
  }

  .products .product {
    background-color: $dark-light-color !important;
  }

  .bottom-nav.active {
    background-color: #171717ea !important;

    @supports ((backdrop-filter: blur(5px))) {
      background-color: #171717c9 !important;
    }
  }
}
</style>
